import React, { useState, useEffect } from 'react';
import { loadStripe } from '@stripe/stripe-js';
import { Elements, CardNumberElement, CardExpiryElement, CardCvcElement, useStripe, useElements } from '@stripe/react-stripe-js';
import whiteLabelService from '../../services/whiteLabelService';
import { config } from '../../config';
import './scss/WhiteLabelPaymentForm.scss';

const PaymentForm = ({ onSuccess, onError }) => {
  const stripe = useStripe();
  const elements = useElements();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [focusedElement, setFocusedElement] = useState(null);
  const [billingCycle, setBillingCycle] = useState('yearly');

  // Get pricing information
  const pricingInfo = whiteLabelService.getPricingInfo();

  const handleSubmit = async (event) => {
    event.preventDefault();

    if (!stripe || !elements) {
      return;
    }

    setLoading(true);
    setError('');

    try {
      // Create payment intent with selected billing cycle
      const { clientSecret } = await whiteLabelService.createPaymentIntent(billingCycle);

      // Confirm payment using CardNumberElement (following your pattern)
      const { error: stripeError, paymentIntent } = await stripe.confirmCardPayment(clientSecret, {
        payment_method: {
          card: elements.getElement(CardNumberElement),
        }
      });

      if (stripeError) {
        setError(stripeError.message);
        onError?.(stripeError.message);
      } else if (paymentIntent.status === 'succeeded') {
        onSuccess?.();
      }
    } catch (err) {
      const errorMsg = err.message || 'Payment failed';
      setError(errorMsg);
      onError?.(errorMsg);
    } finally {
      setLoading(false);
    }
  };

  // CSS variable helper (following your pattern)
  function getCSSVar(name) {
    return getComputedStyle(document.documentElement).getPropertyValue(name).trim();
  }

  // Stripe styling (following your exact pattern)
  const stripeStyle = {
    base: {
      marginTop: '4px',
      padding: '0 12px',
      border: 'none',
      height: '35px',
      fontSize: '14px',
      fontWeight: '500',
      fontFamily: 'system-ui',
      color: getCSSVar('--white-text-color-alternative'),
      backgroundColor: 'transparent',
      width: '100%',
      '::placeholder': {
        fontSize: '14px',
      },
    }
  };

  const handleFocus = (elementType) => {
    setFocusedElement(elementType);
  };

  const handleBlur = () => {
    setFocusedElement(null);
  };

  return (
    <form onSubmit={handleSubmit} className="white-label-payment-form">
      <div className="payment-details">
        <h4>Payment information</h4>

        {/* Billing Cycle Selection */}
        <div className="billing-cycle-selection">
          <h5>Choose billing cycle</h5>
          <div className="cycle-options">
            <div
              className={`cycle-option ${billingCycle === 'monthly' ? 'selected' : ''}`}
              onClick={() => setBillingCycle('monthly')}
            >
              <div className="cycle-header">
                <input
                  type="radio"
                  name="billingCycle"
                  value="monthly"
                  checked={billingCycle === 'monthly'}
                  onChange={() => setBillingCycle('monthly')}
                />
                <span className="cycle-name">Monthly</span>
              </div>
              <div className="cycle-price">
                <span className="amount">${pricingInfo.monthly.amount}</span>
                <span className="period">/month</span>
              </div>
            </div>

            <div
              className={`cycle-option ${billingCycle === 'yearly' ? 'selected' : ''}`}
              onClick={() => setBillingCycle('yearly')}
            >
              <div className="cycle-header">
                <input
                  type="radio"
                  name="billingCycle"
                  value="yearly"
                  checked={billingCycle === 'yearly'}
                  onChange={() => setBillingCycle('yearly')}
                />
                <span className="cycle-name">Yearly</span>
                <span className="savings-badge">Save {pricingInfo.yearly.savings}</span>
              </div>
              <div className="cycle-price">
                <span className="amount">${pricingInfo.yearly.amount}</span>
                <span className="period">/year</span>
              </div>
              <div className="cycle-note">
                ${pricingInfo.yearly.monthlyEquivalent}/month billed annually
              </div>
            </div>
          </div>
        </div>

        <div className="amount-display">
          <span className="amount">${pricingInfo[billingCycle].amount}</span>
          <span className="period">/{pricingInfo[billingCycle].period}</span>
        </div>
      </div>

      <div className="payment-form">
        <div className="input-row">
          <label>
            Card Number
            <div className={`stripe-input ${focusedElement === 'cardNumber' ? 'focused' : ''}`}>
              <CardNumberElement
                options={{ style: stripeStyle }}
                onFocus={() => handleFocus('cardNumber')}
                onBlur={handleBlur}
              />
            </div>
          </label>
        </div>
        <div className="input-row">
          <label>
            Expiration Date
            <div className={`stripe-input ${focusedElement === 'cardExpiry' ? 'focused' : ''}`}>
              <CardExpiryElement
                options={{ style: stripeStyle }}
                onFocus={() => handleFocus('cardExpiry')}
                onBlur={handleBlur}
              />
            </div>
          </label>
          <label>
            CVC
            <div className={`stripe-input ${focusedElement === 'cardCvc' ? 'focused' : ''}`}>
              <CardCvcElement
                options={{ style: stripeStyle }}
                onFocus={() => handleFocus('cardCvc')}
                onBlur={handleBlur}
              />
            </div>
          </label>
        </div>
      </div>

      {error && (
        <div className="error-message">
          {error}
        </div>
      )}

      <button
        type="submit"
        disabled={!stripe || loading}
        className="payment-submit-btn"
      >
        {loading ? 'Processing...' : `Pay $${pricingInfo[billingCycle].amount}${billingCycle === 'yearly' ? '/year' : '/month'}`}
      </button>
    </form>
  );
};

const WhiteLabelPaymentForm = ({ onSuccess, onError }) => {
  const [stripePromise, setStripePromise] = useState(null);
  const [stripeLoadError, setStripeLoadError] = useState(false);

  useEffect(() => {
    loadStripe(config.STRIPE_PUBLIC_KEY)
      .then((stripe) => setStripePromise(stripe))
      .catch((err) => {
        console.error("Failed to load Stripe.js", err);
        setStripeLoadError(true);
      });
  }, []);

  if (stripeLoadError) {
    return (
      <div className="stripe-error">
        <h4>⚠️ Payment system unavailable</h4>
        <p>We couldn't load the payment system. Please check your internet connection and try again.</p>
      </div>
    );
  }

  if (!stripePromise) {
    return <p className="loading-text">Loading payment system...</p>;
  }

  return (
    <Elements stripe={stripePromise}>
      <PaymentForm onSuccess={onSuccess} onError={onError} />
    </Elements>
  );
};

export default WhiteLabelPaymentForm;
